import type { TFunction } from "i18next";
import type {
  EngagementAchievementMetric,
  EngagementAchievementMoneyMetric,
  EngagementAchievementsData,
  EngagementCampaignPerformanceRow,
  EngagementChange,
  EngagementEmailKpisData,
  EngagementTimeSeriesRow,
  EngagementWhatsAppKpisData,
} from "src/services/EngagementOverviewAPI";
import { parseApiNumber } from "../../utils/parseApiNumber";
import {
  ENGAGEMENT_CAMPAIGN_BAR_COLORS,
  type CampaignPerformanceDatum,
  type EngagementBusinessImpactMetric,
  type EngagementKpiMetric,
  type TimeSeriesDatum,
  type WebsiteUsersBreakdown,
} from "../data/engagementOverviewTypes";

export const toChangePercent = (
  change: EngagementChange | string | null | undefined,
): number | null => parseApiNumber(change);

const toNumber = (value: unknown): number => parseApiNumber(value) ?? 0;

export const formatEngagementCount = (value: unknown): string =>
  toNumber(value).toLocaleString();

export const formatEngagementRate = (value: unknown, digits = 1): string =>
  `${toNumber(value).toFixed(digits)}%`;

const clampToChartScale = (value: number): number => Math.max(0, Math.min(100, value));

const isMetricRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const readMetricValue = (raw: unknown): unknown =>
  isMetricRecord(raw) && "value" in raw ? raw.value : raw;

const readMetricFormatted = (raw: unknown): string | undefined => {
  if (!isMetricRecord(raw)) {
    return undefined;
  }
  const formatted = raw.formattedValue;
  return typeof formatted === "string" && formatted.trim() ? formatted.trim() : undefined;
};

const readMetricChange = (raw: unknown, siblingChange: unknown): unknown => {
  if (!isMetricRecord(raw)) {
    return siblingChange;
  }
  return raw.percent_change ?? raw.percentChange ?? siblingChange;
};

const displayCount = (raw: unknown): string => {
  const formatted = readMetricFormatted(raw);
  if (formatted) {
    return formatted;
  }
  const value = readMetricValue(raw);
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return formatEngagementCount(value);
};

const displayRate = (raw: unknown): string => {
  const formatted = readMetricFormatted(raw);
  if (formatted) {
    return formatted;
  }
  const value = readMetricValue(raw);
  if (typeof value === "string" && value.trim()) {
    const trimmed = value.trim();
    return trimmed.includes("%") ? trimmed : `${trimmed}%`;
  }
  return formatEngagementRate(value);
};

const kpiChange = (raw: unknown, siblingChange: unknown): number | null =>
  toChangePercent(readMetricChange(raw, siblingChange) as EngagementChange | string | null | undefined);

const achievementValue = (
  metric: EngagementAchievementMetric | EngagementAchievementMoneyMetric | null | undefined,
): string => {
  const formatted = (metric as EngagementAchievementMoneyMetric | null)?.formattedValue;
  if (formatted?.trim()) {
    return formatted;
  }
  const raw: unknown = metric?.value;
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }
  return formatEngagementCount(raw);
};

export const mapAchievements = (
  t: TFunction,
  data: EngagementAchievementsData | null,
): EngagementBusinessImpactMetric[] => {
  if (!data) {
    return [];
  }

  return [
    {
      id: "orders",
      title: t("engagementOverview.mappers.ordersTitle"),
      value: achievementValue(data.total_orders),
      changePercent: toChangePercent(data.total_orders?.percent_change),
    },
    {
      id: "revenue",
      title: t("engagementOverview.mappers.revenueTitle"),
      value: achievementValue(data.total_sales),
      changePercent: toChangePercent(data.total_sales?.percent_change),
    },
    {
      id: "totalUsers",
      title: t("engagementOverview.mappers.totalUsersTitle"),
      value: achievementValue(data.total_users),
      changePercent: toChangePercent(data.total_users?.percent_change),
    },
    {
      id: "totalCustomers",
      title: t("engagementOverview.mappers.totalCustomersTitle"),
      value: achievementValue(data.total_customers),
      changePercent: toChangePercent(data.total_customers?.percent_change),
    },
  ];
};

export const mapWebsiteUsers = (
  t: TFunction,
  data: EngagementAchievementsData | null,
): WebsiteUsersBreakdown | null => {
  const total = toNumber(data?.total_users?.value);
  if (!data || total <= 0) {
    return null;
  }

  return {
    totalUsers: total,
    slices: [
      {
        name: t("engagementOverview.mappers.websiteTotalUsersSlice"),
        value: total,
        color: ENGAGEMENT_CAMPAIGN_BAR_COLORS.openRate,
        legendPercent: 100,
      },
    ],
  };
};

const EMAIL_KPI_ORDER = [
  "sent",
  "opened",
  "clicks",
  "conversions",
  "totalUsersFromEmail",
  "bounce",
  "clickThrough",
  "openRate",
] as const;

export const mapEmailKpis = (
  t: TFunction,
  data: EngagementEmailKpisData | null,
): EngagementKpiMetric[] => {
  if (!data) {
    return [];
  }

  const byId: Record<(typeof EMAIL_KPI_ORDER)[number], EngagementKpiMetric> = {
    sent: {
      id: "sent",
      title: t("engagementOverview.mappers.sentEmailsTitle"),
      value: displayCount(data.sent),
      changePercent: kpiChange(data.sent, data.sent_change),
    },
    opened: {
      id: "opened",
      title: t("engagementOverview.mappers.openedEmailsTitle"),
      value: displayCount(data.opened),
      changePercent: kpiChange(data.opened, data.opened_change),
    },
    clicks: {
      id: "clicks",
      title: t("engagementOverview.mappers.clicksTitle"),
      value: displayCount(data.clicks),
      changePercent: kpiChange(data.clicks, data.clicks_change),
    },
    conversions: {
      id: "conversions",
      title: t("engagementOverview.mappers.conversionsTitle"),
      value: displayCount(data.conversions),
      changePercent: kpiChange(data.conversions, data.conversions_change),
    },
    totalUsersFromEmail: {
      id: "totalUsersFromEmail",
      title: t("engagementOverview.mappers.totalUsersFromEmailTitle"),
      value: displayCount(data.total_users),
      changePercent: kpiChange(data.total_users, data.total_users_change),
    },
    bounce: {
      id: "bounce",
      title: t("engagementOverview.mappers.bounceRateTitle"),
      value: displayRate(data.bounce_rate),
      changePercent: kpiChange(data.bounce_rate, data.bounce_rate_change),
      format: "percent",
    },
    clickThrough: {
      id: "clickThrough",
      title: t("engagementOverview.mappers.clickThroughRateTitle"),
      value: displayRate(data.click_through_rate),
      changePercent: kpiChange(data.click_through_rate, data.click_through_rate_change),
      format: "percent",
    },
    openRate: {
      id: "openRate",
      title: t("engagementOverview.mappers.openRateTitle"),
      value: displayRate(data.open_rate),
      changePercent: kpiChange(data.open_rate, data.open_rate_change),
      format: "percent",
    },
  };

  return EMAIL_KPI_ORDER.map(id => byId[id]);
};

export const mapEmailEngagementRow = (
  t: TFunction,
  data: EngagementEmailKpisData | null,
): EngagementKpiMetric[] =>
  mapEmailKpis(t, data).filter(metric =>
    ["sent", "opened", "clicks", "conversions"].includes(metric.id),
  );

const WHATSAPP_KPI_ORDER = [
  "sent",
  "delivery",
  "read",
  "click",
  "reply",
  "conversion",
  "block",
  "bounce",
] as const;

export const mapWhatsAppKpis = (
  t: TFunction,
  data: EngagementWhatsAppKpisData | null,
): EngagementKpiMetric[] => {
  if (!data) {
    return [];
  }

  const byId: Record<(typeof WHATSAPP_KPI_ORDER)[number], EngagementKpiMetric> = {
    sent: {
      id: "sent",
      title: t("engagementOverview.mappers.totalMessagesSentTitle"),
      value: displayCount(data.sent),
      changePercent: kpiChange(data.sent, data.sent_change),
    },
    delivery: {
      id: "delivery",
      title: t("engagementOverview.mappers.deliveryRateTitle"),
      value: displayRate(data.delivery_rate),
      changePercent: kpiChange(data.delivery_rate, data.delivery_rate_change),
      format: "percent",
    },
    read: {
      id: "read",
      title: t("engagementOverview.mappers.readRateTitle"),
      value: displayRate(data.read_rate),
      changePercent: kpiChange(data.read_rate, data.read_rate_change),
      format: "percent",
    },
    click: {
      id: "click",
      title: t("engagementOverview.mappers.clickRateTitle"),
      value: displayRate(data.click_rate),
      changePercent: kpiChange(data.click_rate, data.click_rate_change),
      format: "percent",
    },
    reply: {
      id: "reply",
      title: t("engagementOverview.mappers.replyRateTitle"),
      value: displayRate(data.reply_rate),
      changePercent: kpiChange(data.reply_rate, data.reply_rate_change),
      format: "percent",
    },
    conversion: {
      id: "conversion",
      title: t("engagementOverview.mappers.conversionRateTitle"),
      value: displayRate(data.conversion_rate),
      changePercent: kpiChange(data.conversion_rate, data.conversion_rate_change),
      format: "percent",
    },
    block: {
      id: "block",
      title: t("engagementOverview.mappers.blockRateTitle"),
      value: displayRate(data.block_rate),
      changePercent: kpiChange(data.block_rate, data.block_rate_change),
      format: "percent",
    },
    bounce: {
      id: "bounce",
      title: t("engagementOverview.mappers.bounceRateTitle"),
      value: displayRate(data.bounce_rate),
      changePercent: kpiChange(data.bounce_rate, data.bounce_rate_change),
      format: "percent",
    },
  };

  return WHATSAPP_KPI_ORDER.map(id => byId[id]);
};

/** WhatsApp Engagement row on the All Campaigns page — Sent, Delivery, Read, Click. */
export const mapWhatsAppEngagementRow = (
  t: TFunction,
  data: EngagementWhatsAppKpisData | null,
): EngagementKpiMetric[] =>
  mapWhatsAppKpis(t, data).filter(metric =>
    ["sent", "delivery", "read", "click"].includes(metric.id),
  );


export const mapCampaignPerformance = (
  t: TFunction,
  rows: EngagementCampaignPerformanceRow[] | null | undefined,
): CampaignPerformanceDatum[] => {
  const safeRows = rows ?? [];
  const maxSent = safeRows.reduce((max, row) => Math.max(max, toNumber(row?.sent)), 0);
  const untitledCampaign = t("engagementOverview.mappers.untitledCampaign");

  const nameTotals = new Map<string, number>();
  safeRows.forEach(row => {
    const name = row?.campaign_name?.trim() || untitledCampaign;
    nameTotals.set(name, (nameTotals.get(name) ?? 0) + 1);
  });

  const nameSeen = new Map<string, number>();

  return safeRows.map((row, index) => {
    const name = row?.campaign_name?.trim() || untitledCampaign;
    const occurrence = (nameSeen.get(name) ?? 0) + 1;
    nameSeen.set(name, occurrence);
    const label = (nameTotals.get(name) ?? 0) > 1 ? `${name} (${occurrence})` : name;

    const sent = toNumber(row?.sent);
    const openRate = toNumber(row?.open_rate);
    const clickRate = toNumber(row?.click_rate);
    const clickThroughRate = toNumber(row?.click_through_rate);

    return {
      id: row?.campaign_id || `${label}-${index}`,
      label,
      medium: row?.medium ?? null,
      openRate,
      clickRate,
      totalSent: sent,
      clickThroughRate,
      openRateValue: clampToChartScale(openRate),
      clickRateValue: clampToChartScale(clickRate),
      totalSentValue: maxSent > 0 ? clampToChartScale((sent / maxSent) * 100) : 0,
      clickThroughRateValue: clampToChartScale(clickThroughRate),
    } satisfies CampaignPerformanceDatum;
  });
};

interface TimeSeriesAccumulator {
  date: string;
  sent: number;
  opened: number;
  clicks: number;
  openRateWeighted: number;
  clickRateWeighted: number;
  clickThroughRateWeighted: number;
  bounceRateWeighted: number;
  openRateSum: number;
  clickRateSum: number;
  clickThroughRateSum: number;
  bounceRateSum: number;
  rows: number;
}

const emptyAccumulator = (date: string): TimeSeriesAccumulator => ({
  date,
  sent: 0,
  opened: 0,
  clicks: 0,
  openRateWeighted: 0,
  clickRateWeighted: 0,
  clickThroughRateWeighted: 0,
  bounceRateWeighted: 0,
  openRateSum: 0,
  clickRateSum: 0,
  clickThroughRateSum: 0,
  bounceRateSum: 0,
  rows: 0,
});

const accumulate = (
  bucket: TimeSeriesAccumulator,
  sent: number,
  opened: number,
  clicks: number,
  openRate: number,
  clickRate: number,
  clickThroughRate: number,
  bounceRate: number,
): void => {
  bucket.sent += sent;
  bucket.opened += opened;
  bucket.clicks += clicks;
  bucket.openRateWeighted += openRate * sent;
  bucket.clickRateWeighted += clickRate * sent;
  bucket.clickThroughRateWeighted += clickThroughRate * sent;
  bucket.bounceRateWeighted += bounceRate * sent;
  bucket.openRateSum += openRate;
  bucket.clickRateSum += clickRate;
  bucket.clickThroughRateSum += clickThroughRate;
  bucket.bounceRateSum += bounceRate;
  bucket.rows += 1;
};

const finalizeBucket = (bucket: TimeSeriesAccumulator): TimeSeriesDatum => {
  const rate = (weighted: number, plainSum: number) => {
    if (bucket.sent > 0) {
      return weighted / bucket.sent;
    }
    return bucket.rows > 0 ? plainSum / bucket.rows : 0;
  };

  return {
    date: bucket.date,
    sent: bucket.sent,
    opened: bucket.opened,
    clicks: bucket.clicks,
    openRate: rate(bucket.openRateWeighted, bucket.openRateSum),
    clickRate: rate(bucket.clickRateWeighted, bucket.clickRateSum),
    clickThroughRate: rate(bucket.clickThroughRateWeighted, bucket.clickThroughRateSum),
    bounceRate: rate(bucket.bounceRateWeighted, bucket.bounceRateSum),
  };
};

export const mapTimeSeries = (
  rows: EngagementTimeSeriesRow[] | null | undefined,
): TimeSeriesDatum[] => {
  const buckets = new Map<string, TimeSeriesAccumulator>();

  (rows ?? []).forEach(row => {
    const date = row?.date;
    if (!date) {
      return;
    }
    const bucket = buckets.get(date) ?? emptyAccumulator(date);
    accumulate(
      bucket,
      toNumber(row?.sent),
      toNumber(row?.opened),
      toNumber(row?.clicks),
      toNumber(row?.open_rate),
      toNumber(row?.click_rate),
      toNumber(row?.click_through_rate),
      toNumber(row?.bounce_rate),
    );
    buckets.set(date, bucket);
  });

  return Array.from(buckets.values())
    .sort((left, right) => left.date.localeCompare(right.date))
    .map(finalizeBucket);
};

export const aggregateTimeSeriesMonthly = (series: TimeSeriesDatum[]): TimeSeriesDatum[] => {
  const buckets = new Map<string, TimeSeriesAccumulator>();

  (series ?? []).forEach(point => {
    const monthKey = `${point.date.slice(0, 7)}-01`;
    const bucket = buckets.get(monthKey) ?? emptyAccumulator(monthKey);
    accumulate(
      bucket,
      point.sent,
      point.opened,
      point.clicks,
      point.openRate,
      point.clickRate,
      point.clickThroughRate,
      point.bounceRate,
    );
    buckets.set(monthKey, bucket);
  });

  return Array.from(buckets.values())
    .sort((left, right) => left.date.localeCompare(right.date))
    .map(finalizeBucket);
};
